import { useState } from "react";
import imageCompression from "browser-image-compression";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";

interface ImageQualitySettings {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  preset: 'low' | 'medium' | 'high' | 'ultra';
}

// Quality presets based on subscription plans
const QUALITY_PRESETS: Record<string, ImageQualitySettings> = {
  low: {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1280,
    preset: 'low'
  },
  medium: {
    maxSizeMB: 1.0,
    maxWidthOrHeight: 1920,
    preset: 'medium'
  },
  high: {
    maxSizeMB: 2.0,
    maxWidthOrHeight: 2560,
    preset: 'high'
  },
  ultra: {
    maxSizeMB: 3.0,
    maxWidthOrHeight: 3840,
    preset: 'ultra'
  }
};

export const useTimelineImageUpload = (timelineUserId: string) => {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch timeline-specific image quality settings
  const { data: timelineSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['timeline-image-settings', timelineUserId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profile_timeline')
        .select(`
          image_quality_preset,
          custom_max_size_mb,
          custom_max_resolution
        `)
        .eq('user_id', timelineUserId)
        .single();

      if (error) {
        console.error('Error fetching timeline settings:', error);
        // Fallback to medium quality
        return {
          image_quality_preset: 'medium' as const,
          custom_max_size_mb: 1.0,
          custom_max_resolution: 1920
        };
      }

      return data;
    },
    enabled: !!timelineUserId
  });

  const getImageQualitySettings = (): ImageQualitySettings => {
    if (!timelineSettings) {
      return QUALITY_PRESETS.medium; // Default fallback
    }

    // Use custom settings if available, otherwise use preset
    const preset = timelineSettings.image_quality_preset || 'medium';
    const baseSettings = QUALITY_PRESETS[preset];

    return {
      maxSizeMB: timelineSettings.custom_max_size_mb || baseSettings.maxSizeMB,
      maxWidthOrHeight: timelineSettings.custom_max_resolution || baseSettings.maxWidthOrHeight,
      preset: preset as any
    };
  };

  const compressImage = async (file: File): Promise<File> => {
    const qualitySettings = getImageQualitySettings();
    
    const options = {
      maxSizeMB: qualitySettings.maxSizeMB,
      maxWidthOrHeight: qualitySettings.maxWidthOrHeight,
      useWebWorker: true,
      fileType: 'image/webp',
      onProgress: (progress: number) => {
        setUploadProgress(progress * 0.5); // First 50% for compression
      }
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      throw new Error('Fehler beim Komprimieren des Bildes');
    }
  };

  const uploadImages = async (files: File[]): Promise<string[]> => {
    if (!user) {
      throw new Error('User must be authenticated to upload images');
    }

    if (files.length === 0) return [];

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadedUrls: string[] = [];
      const qualitySettings = getImageQualitySettings();

      // Show quality info to user
      toast.info(`Bilder werden in ${qualitySettings.preset.toUpperCase()} Qualität hochgeladen (${qualitySettings.maxSizeMB}MB, ${qualitySettings.maxWidthOrHeight}px)`);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Compress image with timeline-specific settings
        const compressedFile = await compressImage(file);
        
        // Generate unique filename
        const fileExt = compressedFile.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        // Upload to Supabase Storage
        const { data, error } = await supabase.storage
          .from('timeline-photos')
          .upload(filePath, compressedFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          throw error;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('timeline-photos')
          .getPublicUrl(data.path);

        uploadedUrls.push(publicUrl);
        
        // Update progress
        setUploadProgress(50 + ((i + 1) / files.length) * 50);
      }

      toast.success(`${uploadedUrls.length} Bild${uploadedUrls.length > 1 ? 'er' : ''} erfolgreich hochgeladen`);
      return uploadedUrls;
    } catch (error: any) {
      console.error('Error uploading images:', error);
      toast.error(`Fehler beim Hochladen: ${error.message}`);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getAvailableQualityPresets = (): Array<{ key: string; label: string; maxSizeMB: number; maxRes: number }> => {
    return Object.entries(QUALITY_PRESETS).map(([key, settings]) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      maxSizeMB: settings.maxSizeMB,
      maxRes: settings.maxWidthOrHeight
    }));
  };

  return {
    uploadImages,
    isUploading,
    uploadProgress,
    isLoadingSettings,
    currentQualitySettings: getImageQualitySettings(),
    availablePresets: getAvailableQualityPresets(),
    timelineSettings
  };
};